import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getClients, getSLAPolicies, createContact,
  getContacts, getAgents,
} from "../api";
import {
  Plus, Building2, Users, Ticket, Monitor,
  ChevronDown, ChevronUp, CheckCircle, ChevronRight,
  Search, Activity, ShieldCheck, AlertTriangle,
  ArrowUpDown, Filter, BarChart3, Cloud, Sparkles,
} from "lucide-react";
import { tokens, sx, FullPageSpinner, Panel, KpiCard, MiniAvatar } from "../theme";
import useIsMobile from "../hooks/useIsMobile";

const API   = "https://ticketing.midtc.com/api";
const token = () => localStorage.getItem("midtc_token") || "";

const inputStyle = { ...sx.input, padding: "9px 12px", fontSize: 14 };
const normalizeArray = (d) =>
  Array.isArray(d) ? d : d?.data || d?.contacts || d?.rows || [];

// ─────────────────────────────────────────────────────────────
//  M365 CREDENTIALS MODAL
// ─────────────────────────────────────────────────────────────
function M365CredentialsModal({ clientId, clientName, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const [creds, setCreds] = useState({ azure_tenant_id:"", azure_client_id:"", azure_client_secret:"" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState("");

  const handleTest = async () => {
    if (!creds.azure_tenant_id || !creds.azure_client_id || !creds.azure_client_secret)
      return setError("Fill in all three fields before testing.");
    setTesting(true); setError(""); setTestResult(null);
    try {
      const res  = await fetch(`${API}/o365/test-credentials`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body: JSON.stringify(creds),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Connection failed");
      setTestResult({ ok:true, tenant_name:data.tenant_name, licensed_users:data.licensed_users, domains:data.domains });
    } catch (err) { setError(err.message); setTestResult({ ok:false }); }
    finally { setTesting(false); }
  };

  const handleSave = async () => {
    if (!creds.azure_tenant_id || !creds.azure_client_id || !creds.azure_client_secret)
      return setError("All three fields are required.");
    setSaving(true); setError("");
    try {
      const res  = await fetch(`${API}/clients/${clientId}/o365-credentials`, {
        method:  "PATCH",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body: JSON.stringify(creds),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save credentials.");
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"var(--t-surface-deep)",border:"1px solid var(--t-border-mid)",borderRadius:16,width:"100%",maxWidth:520,boxShadow:"0 32px 80px rgba(0,0,0,0.6)"}}>
        <div style={{padding:"18px 24px",borderBottom:"1px solid var(--t-border-mid)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontWeight:700,fontSize:15,color:"var(--t-text-primary)"}}>Configure M365 App Registration</div>
            <div style={{fontSize:12,color:"var(--t-text-label)",marginTop:2}}>{clientName}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--t-text-label)",fontSize:20,cursor:"pointer"}}>×</button>
        </div>
        <div style={{padding:24}}>
          {error && <div style={{background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"10px 14px",color:"#f87171",fontSize:13,marginBottom:16}}>⚠ {error}</div>}
          <div style={{background:"rgba(29,78,216,0.08)",border:"1px solid rgba(29,78,216,0.25)",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"var(--badge-blue)",lineHeight:1.7}}>
            <div style={{fontWeight:700,marginBottom:4}}>Create an app in Microsoft Entra ID:</div>
            <div>1. portal.azure.com → Entra ID → App Registrations → New Registration</div>
            <div>2. API Permissions → Add Application permissions: User.Read.All, Directory.Read.All, UserAuthenticationMethod.ReadWrite.All, MailboxSettings.ReadWrite → Grant Admin Consent</div>
            <div>3. Certificates &amp; Secrets → New Client Secret → copy the value</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div>
              <label style={{fontSize:12,color:"var(--t-text-label)",display:"block",marginBottom:5}}>Azure Tenant ID</label>
              <input style={{...inputStyle,fontFamily:"monospace"}} value={creds.azure_tenant_id} onChange={e=>setCreds(p=>({...p,azure_tenant_id:e.target.value}))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/>
            </div>
            <div>
              <label style={{fontSize:12,color:"var(--t-text-label)",display:"block",marginBottom:5}}>Application (Client) ID</label>
              <input style={{...inputStyle,fontFamily:"monospace"}} value={creds.azure_client_id} onChange={e=>setCreds(p=>({...p,azure_client_id:e.target.value}))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/>
            </div>
            <div>
              <label style={{fontSize:12,color:"var(--t-text-label)",display:"block",marginBottom:5}}>Client Secret Value</label>
              <input type="password" style={inputStyle} value={creds.azure_client_secret} onChange={e=>setCreds(p=>({...p,azure_client_secret:e.target.value}))} placeholder="••••••••••••••••"/>
            </div>
          </div>
          {testResult?.ok && (
            <div style={{marginTop:12,background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.2)",borderRadius:8,padding:"10px 14px",fontSize:13,color:"var(--badge-green)",display:"flex",alignItems:"center",gap:8}}>
              <CheckCircle size={14}/> Connected — {testResult.tenant_name} · {testResult.licensed_users} licensed users · {testResult.domains?.length} domain(s)
            </div>
          )}
        </div>
        <div style={{padding:isMobile?"16px":"16px 24px",borderTop:"1px solid var(--t-border-mid)",display:"flex",gap:10,justifyContent:"flex-end",flexWrap:isMobile?"wrap":"nowrap"}}>
          <button onClick={onClose} style={{background:"transparent",color:"var(--t-text-muted)",border:"1px solid var(--t-border-strong)",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={handleTest} disabled={testing} style={{background:"#0f4c8a",color:"#fff",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer"}}>
            {testing?"Testing…":"🔌 Test Connection"}
          </button>
          <button onClick={handleSave} disabled={saving} style={{background:"#4f6fff",color:"#fff",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer"}}>
            {saving?"Saving…":"💾 Save & Import Users"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  M365 IMPORT MODAL
// ─────────────────────────────────────────────────────────────
function M365ImportModal({ clientId, clientName, onClose, onImported }) {
  const isMobile = useIsMobile();
  const [step, setStep]                     = useState("loading");
  const [error, setError]                   = useState("");
  const [preview, setPreview]               = useState(null);
  const [selected, setSelected]             = useState(new Set());
  const [updateDomain, setUpdateDomain]     = useState(false);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [sendWelcome,  setSendWelcome]      = useState(true);
  const [result, setResult]                 = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API}/clients/${clientId}/import-m365/users`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const data = await res.json();
        if (!res.ok) { setError(data.error || "Failed to fetch users."); setStep("error"); return; }
        setPreview(data);
        setSelected(new Set(data.users.filter(u => !u.alreadyExists).map(u => u.email)));
        if (data.domains?.length) setSelectedDomain(data.domains[0]);
        setStep("select");
      } catch (err) { setError("Network error: " + err.message); setStep("error"); }
    })();
  }, [clientId]);

  const handleConfirm = async () => {
    const usersToImport = (preview?.users || []).filter(u => selected.has(u.email));
    if (!usersToImport.length) { setError("Select at least one user."); return; }
    setError(""); setStep("importing");
    try {
      const res  = await fetch(`${API}/clients/${clientId}/import-m365/confirm`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:JSON.stringify({ users:usersToImport, updateDomain, domain:updateDomain?selectedDomain:undefined, sendWelcomeEmails:sendWelcome }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Import failed."); setStep("select"); return; }
      setResult(data); setStep("done");
      if (onImported) onImported();
    } catch (err) { setError("Network error: " + err.message); setStep("select"); }
  };

  const toggleUser    = (email) => setSelected(p => { const n = new Set(p); n.has(email) ? n.delete(email) : n.add(email); return n; });
  const selectOnlyNew = ()      => setSelected(new Set((preview?.users||[]).filter(u=>!u.alreadyExists).map(u=>u.email)));
  const toggleAll     = ()      => {
    const all = preview?.users||[];
    setSelected(all.every(u=>selected.has(u.email)) ? new Set() : new Set(all.map(u=>u.email)));
  };

  const S = {
    overlay: { position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 },
    modal:   { background:"var(--t-surface-mid)",borderRadius:12,width:"100%",maxWidth:720,maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 64px rgba(0,0,0,0.5)",overflow:"hidden",border:"1px solid var(--t-border-strong)" },
    header:  { background:"#4f6fff",padding:"18px 24px",display:"flex",alignItems:"center",justifyContent:"space-between" },
    body:    { padding:24,overflowY:"auto",flex:1 },
    footer:  { padding:"14px 24px",borderTop:"1px solid #334155",display:"flex",gap:10,justifyContent:"flex-end",background:"var(--t-surface-deep)" },
    btnBlue: { background:"#4f6fff",color:"#fff",border:"none",padding:"10px 22px",borderRadius:7,fontWeight:600,cursor:"pointer",fontSize:14 },
    btnGreen:{ background:"#16a34a",color:"#fff",border:"none",padding:"10px 22px",borderRadius:7,fontWeight:600,cursor:"pointer",fontSize:14 },
    btnGhost:{ background:"transparent",color:"var(--t-text-muted)",border:"1px solid var(--t-border-strong)",padding:"10px 18px",borderRadius:7,fontWeight:600,cursor:"pointer",fontSize:14 },
    error:   { background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"12px 14px",color:"#f87171",fontSize:13,marginBottom:16 },
    badge:   (c) => ({ background:c+"22",color:c,border:`1px solid ${c}44`,borderRadius:999,padding:"2px 9px",fontSize:12,fontWeight:600,whiteSpace:"nowrap" }),
    statCard:{ background:"var(--t-surface-deep)",border:"1px solid var(--t-border-strong)",borderRadius:8,padding:14,textAlign:"center",flex:1 },
    row:     (sel) => ({ display:"grid",gridTemplateColumns:"32px 1fr 160px 90px",alignItems:"center",padding:"9px 12px",borderBottom:"1px solid var(--t-border-mid)",gap:8,background:sel?"rgba(29,78,216,0.15)":"transparent",cursor:"pointer",...(isMobile?{minWidth:460}:{}) }),
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.modal}>
        <div style={S.header}>
          <div>
            <div style={{fontWeight:700,fontSize:16,color:"#fff"}}>Import Users from Microsoft 365</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",marginTop:2}}>{clientName}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#fff",fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={S.body}>
          {error && <div style={S.error}>⚠ {error}</div>}
          {step==="loading" && <div style={{textAlign:"center",padding:"48px 0"}}><div style={{fontSize:40,marginBottom:16}}>⏳</div><h3 style={{margin:"0 0 8px",color:"var(--t-text-primary)"}}>Loading users from Microsoft 365...</h3><p style={{color:"var(--t-text-muted)",fontSize:14}}>Fetching licensed users from your tenant.</p></div>}
          {step==="error"   && <div style={{textAlign:"center",padding:"48px 0"}}><div style={{fontSize:40,marginBottom:16}}>❌</div><h3 style={{margin:"0 0 8px",color:"#f87171"}}>Failed to load users</h3><p style={{color:"var(--t-text-muted)",fontSize:14}}>{error}</p></div>}
          {step==="select"&&preview&&(
            <div>
              <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:isMobile?"wrap":"nowrap"}}>
                {[{label:"Licensed Users",value:preview.totalFound,color:"#60a5fa"},{label:"New to Import",value:preview.newUsers,color:"var(--badge-green)"},{label:"Already Exists",value:preview.existing,color:"var(--t-text-muted)"}].map(s=>(
                  <div key={s.label} style={S.statCard}><div style={{fontSize:28,fontWeight:700,color:s.color}}>{s.value}</div><div style={{fontSize:12,color:"var(--t-text-label)",marginTop:4}}>{s.label}</div></div>
                ))}
              </div>
              {preview.domains?.length>0&&(
                <div style={{background:"rgba(22,163,74,0.1)",border:"1px solid rgba(22,163,74,0.3)",borderRadius:8,padding:"12px 14px",marginBottom:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",fontSize:14}}>
                  <input type="checkbox" id="updDomain" checked={updateDomain} onChange={e=>setUpdateDomain(e.target.checked)}/>
                  <label htmlFor="updDomain" style={{cursor:"pointer",color:"var(--badge-green)",fontWeight:600}}>Update client domain to:</label>
                  <select value={selectedDomain} onChange={e=>setSelectedDomain(e.target.value)} style={{...inputStyle,width:"auto",padding:"4px 8px",opacity:updateDomain?1:0.5}}>
                    {preview.domains.map(d=><option key={d}>{d}</option>)}
                  </select>
                </div>
              )}
              <div style={{background:"rgba(249,115,22,0.1)",border:"1px solid rgba(249,115,22,0.3)",borderRadius:8,padding:"12px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:10,fontSize:14}}>
                <input type="checkbox" id="sendWelcome" checked={sendWelcome} onChange={e=>setSendWelcome(e.target.checked)}/>
                <label htmlFor="sendWelcome" style={{cursor:"pointer",color:"#f97316",fontWeight:600}}>Send welcome email to newly imported users</label>
                <span style={{fontSize:12,color:"var(--t-text-label)",marginLeft:"auto"}}>Existing users won't be re-emailed</span>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
                <button onClick={selectOnlyNew} style={{...S.btnGhost,fontSize:12,padding:"6px 12px"}}>New Only ({preview.newUsers})</button>
                <button onClick={toggleAll}     style={{...S.btnGhost,fontSize:12,padding:"6px 12px"}}>Toggle All</button>
                <span style={{marginLeft:"auto",fontSize:13,color:"var(--t-text-label)"}}><strong style={{color:"#60a5fa"}}>{selected.size}</strong> of {preview.totalFound} selected</span>
              </div>
              <div style={{border:"1px solid var(--t-border-strong)",borderRadius:8,overflow:"hidden",maxHeight:360,overflowY:"auto",...(isMobile?{overflowX:"auto"}:{})}}>
                <div style={{background:"var(--t-surface-deep)",padding:"9px 12px",borderBottom:"1px solid #334155",display:"grid",gridTemplateColumns:"32px 1fr 160px 90px",gap:8,fontSize:11,fontWeight:700,color:"var(--t-text-deep)",textTransform:"uppercase",...(isMobile?{minWidth:460}:{})}}>
                  <span/><span>Name / Email</span><span>Job Title</span><span>Status</span>
                </div>
                {preview.users.map(u=>(
                  <div key={u.email} style={S.row(selected.has(u.email))} onClick={()=>toggleUser(u.email)}>
                    <input type="checkbox" checked={selected.has(u.email)} onChange={()=>{}} style={{margin:0,pointerEvents:"none",accentColor:"#4f6fff"}}/>
                    <div><div style={{fontSize:14,fontWeight:500,color:"var(--t-text-primary)"}}>{u.firstName} {u.lastName}</div><div style={{fontSize:12,color:"var(--t-text-deep)"}}>{u.email}</div></div>
                    <div style={{fontSize:13,color:"var(--t-text-label)"}}>{u.jobTitle||"—"}</div>
                    <span style={u.alreadyExists?S.badge("#94a3b8"):S.badge("#4ade80")}>{u.alreadyExists?"Exists":"New"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {step==="importing"&&<div style={{textAlign:"center",padding:"48px 0"}}><div style={{fontSize:40,marginBottom:16}}>⏳</div><h3 style={{margin:"0 0 8px",color:"var(--t-text-primary)"}}>Importing {selected.size} users...</h3></div>}
          {step==="done"&&result&&(
            <div style={{textAlign:"center",padding:"32px 0"}}>
              <div style={{fontSize:52,marginBottom:12}}>✅</div>
              <h3 style={{margin:"0 0 8px",color:"var(--badge-green)",fontSize:18}}>Import Complete</h3>
              <p style={{color:"var(--t-text-muted)",marginBottom:24}}>{result.message}</p>
              <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:isMobile?"wrap":"nowrap"}}>
                {[{label:"Added",value:result.imported,color:"var(--badge-green)"},{label:"Updated",value:result.updated,color:"#60a5fa"},{label:"Skipped",value:result.skipped,color:"var(--t-text-muted)"}].map(s=>(
                  <div key={s.label} style={{...S.statCard,minWidth:100}}><div style={{fontSize:28,fontWeight:700,color:s.color}}>{s.value}</div><div style={{fontSize:12,color:"var(--t-text-label)"}}>{s.label}</div></div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div style={S.footer}>
          {step==="done"
            ? <button onClick={onClose} style={S.btnBlue}>Done</button>
            : <><button onClick={onClose} style={S.btnGhost} disabled={["loading","importing"].includes(step)}>Cancel</button>
               {step==="select"&&<button onClick={handleConfirm} style={S.btnGreen} disabled={!selected.size}>Import {selected.size} User{selected.size!==1?"s":""} →</button>}</>
          }
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  NEW CLIENT WIZARD
// ─────────────────────────────────────────────────────────────
const WIZARD_STEPS = [
  { id:"basic",    label:"Basic Info",      icon:"🏢" },
  { id:"m365",     label:"M365 Connector",  icon:"☁️" },
  { id:"domain",   label:"Domain & Users",  icon:"🌐" },
  { id:"branding", label:"Portal Branding", icon:"🎨" },
  { id:"assign",   label:"Manager & SLA",   icon:"👤" },
  { id:"review",   label:"Review",          icon:"✅" },
];

function NewClientWizard({ onClose, onCreated, policies, agents }) {
  const isMobile = useIsMobile();
  const [step,           setStep]           = useState(0);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState("");
  const [testing,        setTesting]        = useState(false);
  const [testResult,     setTestResult]     = useState(null);
  const [createdClientId,setCreatedClientId]= useState(null);
  const [showImport,     setShowImport]     = useState(false);

  const [basic,    setBasic]    = useState({ company_name:"", phone:"", address:"", city:"", state:"", zip:"", notes:"" });
  const [m365,     setM365]     = useState({ enabled:false, azure_tenant_id:"", azure_client_id:"", azure_client_secret:"" });
  const [domain,   setDomain]   = useState({ domain:"", domains:[] });
  const [branding, setBranding] = useState({ primary_color:"#4f6fff", accent_color:"#0f172a", welcome_msg:"Welcome to the support portal.", portal_slug:"" });
  const [assign,   setAssign]   = useState({ sla_policy_id:"", account_manager:"" });

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
  const setB  = (k,v) => setBasic(p=>({...p,[k]:v}));
  const setM  = (k,v) => setM365(p=>({...p,[k]:v}));
  const setD  = (k,v) => setDomain(p=>({...p,[k]:v}));
  const setBr = (k,v) => setBranding(p=>({...p,[k]:v}));
  const setA  = (k,v) => setAssign(p=>({...p,[k]:v}));

  useEffect(() => {
    if (basic.company_name && !branding.portal_slug) setBr("portal_slug", slugify(basic.company_name));
  }, [basic.company_name]); // eslint-disable-line

  const testM365 = async () => {
    if (!m365.azure_tenant_id || !m365.azure_client_id || !m365.azure_client_secret) { setError("Fill in all three M365 fields before testing."); return; }
    setTesting(true); setError(""); setTestResult(null);
    try {
      const res  = await fetch(`${API}/o365/test-credentials`, { method:"POST", headers:{"Content-Type":"application/json",Authorization:`Bearer ${token()}`}, body:JSON.stringify({ azure_tenant_id:m365.azure_tenant_id, azure_client_id:m365.azure_client_id, azure_client_secret:m365.azure_client_secret }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Connection failed");
      setTestResult({ ok:true, domains:data.domains, tenant_name:data.tenant_name, licensed_users:data.licensed_users });
      setD("domains", data.domains);
      if (data.domains?.length) setD("domain", data.domains.find(d=>!d.includes("onmicrosoft"))||data.domains[0]);
    } catch (err) { setError("Connection failed: "+err.message); setTestResult({ ok:false }); }
    finally { setTesting(false); }
  };

  const handleCreate = async () => {
    setSaving(true); setError("");
    try {
      const clientRes  = await fetch(`${API}/clients`, { method:"POST", headers:{"Content-Type":"application/json",Authorization:`Bearer ${token()}`}, body:JSON.stringify({ company_name:basic.company_name, phone:basic.phone, address:basic.address, city:basic.city, state:basic.state, zip:basic.zip, notes:basic.notes, domain:domain.domain||null, sla_policy_id:assign.sla_policy_id||null, account_manager:assign.account_manager||null }) });
      const clientData = await clientRes.json();
      if (!clientRes.ok) throw new Error(clientData.error || "Failed to create client");
      const clientId = clientData.id;
      setCreatedClientId(clientId);
      if (m365.enabled && m365.azure_tenant_id) {
        await fetch(`${API}/clients/${clientId}/o365-credentials`, { method:"PATCH", headers:{"Content-Type":"application/json",Authorization:`Bearer ${token()}`}, body:JSON.stringify({ azure_tenant_id:m365.azure_tenant_id, azure_client_id:m365.azure_client_id, azure_client_secret:m365.azure_client_secret }) });
      }
      await fetch(`${API}/clients/${clientId}/branding`, { method:"PUT", headers:{"Content-Type":"application/json",Authorization:`Bearer ${token()}`}, body:JSON.stringify({ company_name:basic.company_name, primary_color:branding.primary_color, accent_color:branding.accent_color, welcome_msg:branding.welcome_msg, portal_slug:branding.portal_slug }) });
      setSaving(false); setStep(5);
      if (onCreated) onCreated(clientData);
    } catch (err) { setError(err.message); setSaving(false); }
  };

  const canNext = () => {
    if (step===0) return !!basic.company_name.trim();
    if (step===1) return !m365.enabled||(!!m365.azure_tenant_id&&!!m365.azure_client_id&&!!m365.azure_client_secret);
    return true;
  };

  const S = {
    overlay:  { position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 },
    modal:    { background:"var(--t-surface-deep)",border:"1px solid var(--t-border-mid)",borderRadius:16,width:"100%",maxWidth:680,maxHeight:"92vh",display:"flex",flexDirection:"column",boxShadow:"0 32px 80px rgba(0,0,0,0.6)",overflow:"hidden" },
    header:   { padding:isMobile?"16px":"20px 28px",borderBottom:"1px solid var(--t-border-mid)",background:"var(--t-page)" },
    body:     { padding:isMobile?16:28,overflowY:"auto",flex:1 },
    footer:   { padding:isMobile?"14px 16px":"16px 28px",borderTop:"1px solid var(--t-border-mid)",display:"flex",gap:10,justifyContent:"flex-end",background:"var(--t-page)" },
    stepBar:  { display:"flex",gap:4,marginBottom:24,overflowX:"auto",paddingBottom:4 },
    stepPill: (active,done) => ({ display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:99,fontSize:12,fontWeight:600,whiteSpace:"nowrap", background:done?"#052e16":active?"#1e3a5f":"#1e293b", color:done?"#4ade80":active?"#60a5fa":"#475569", border:`1px solid ${done?"#166534":active?"#4f6fff":"var(--t-border-strong)"}` }),
    label:    { fontSize:12,color:"var(--t-text-label)",display:"block",marginBottom:5 },
    sectionH: { fontSize:13,fontWeight:700,color:"var(--t-text-muted)",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.05em" },
    btnBlue:  { background:"#4f6fff",color:"#fff",border:"none",padding:"10px 22px",borderRadius:8,fontWeight:600,cursor:"pointer",fontSize:14 },
    btnGhost: { background:"transparent",color:"var(--t-text-muted)",border:"1px solid var(--t-border-strong)",padding:"10px 18px",borderRadius:8,fontWeight:600,cursor:"pointer",fontSize:14 },
    grid2:    { display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12 },
    error:    { background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"12px 14px",color:"#f87171",fontSize:13,marginBottom:16 },
    toggle:   (on) => ({ width:42,height:24,borderRadius:12,cursor:"pointer",background:on?"#4f6fff":"var(--t-border-strong)",border:"none",position:"relative",transition:"background .2s",flexShrink:0 }),
    toggleDot:(on) => ({ position:"absolute",top:3,left:on?21:3,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left .2s",pointerEvents:"none" }),
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.modal}>
        <div style={S.header}>
          <div style={{fontWeight:700,fontSize:18,color:"var(--t-text-primary)",marginBottom:14}}>{step<5?"New Client Setup":"Client Created!"}</div>
          <div style={S.stepBar}>
            {WIZARD_STEPS.map((s,i)=>(
              <div key={s.id} style={S.stepPill(i===step,i<step)}>
                {i<step?<CheckCircle size={12}/>:<span>{s.icon}</span>}
                {s.label}
                {i<WIZARD_STEPS.length-1&&<ChevronRight size={10} style={{opacity:0.4}}/>}
              </div>
            ))}
          </div>
        </div>
        <div style={S.body}>
          {error&&<div style={S.error}>⚠ {error}</div>}

          {step===0&&(
            <div>
              <div style={S.sectionH}>Company Information</div>
              <div style={S.grid2}>
                <div style={{gridColumn:"1/-1"}}><label style={S.label}>Company Name *</label><input style={inputStyle} value={basic.company_name} onChange={e=>setB("company_name",e.target.value)} placeholder="Acme Corporation" autoFocus/></div>
                <div><label style={S.label}>Phone</label><input style={inputStyle} value={basic.phone} onChange={e=>setB("phone",e.target.value)} placeholder="(555) 000-0000"/></div>
                <div><label style={S.label}>Address</label><input style={inputStyle} value={basic.address} onChange={e=>setB("address",e.target.value)} placeholder="123 Main St"/></div>
                <div><label style={S.label}>City</label><input style={inputStyle} value={basic.city} onChange={e=>setB("city",e.target.value)}/></div>
                <div><label style={S.label}>State</label><input style={inputStyle} value={basic.state} onChange={e=>setB("state",e.target.value)}/></div>
                <div><label style={S.label}>ZIP</label><input style={inputStyle} value={basic.zip} onChange={e=>setB("zip",e.target.value)}/></div>
                <div style={{gridColumn:"1/-1"}}><label style={S.label}>Internal Notes</label><textarea style={{...inputStyle,resize:"vertical"}} rows={2} value={basic.notes} onChange={e=>setB("notes",e.target.value)}/></div>
              </div>
            </div>
          )}

          {step===1&&(
            <div>
              <div style={S.sectionH}>Microsoft 365 Integration</div>
              <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20,padding:"14px 16px",background:"var(--t-surface-mid)",border:"1px solid var(--t-border-strong)",borderRadius:10}}>
                <button style={S.toggle(m365.enabled)} onClick={()=>setM("enabled",!m365.enabled)}><div style={S.toggleDot(m365.enabled)}/></button>
                <div>
                  <div style={{fontWeight:600,color:"var(--t-text-primary)",fontSize:14}}>{m365.enabled?"Microsoft 365 Enabled":"Manual Setup (No M365)"}</div>
                  <div style={{fontSize:12,color:"var(--t-text-label)",marginTop:2}}>{m365.enabled?"Connect this client's Azure tenant for user import and management.":"Skip M365 — add contacts manually later."}</div>
                </div>
              </div>
              {m365.enabled&&(
                <>
                  <div style={{background:"rgba(29,78,216,0.08)",border:"1px solid rgba(29,78,216,0.25)",borderRadius:8,padding:"12px 16px",marginBottom:16,fontSize:12,color:"var(--badge-blue)",lineHeight:1.8}}>
                    <div style={{fontWeight:700,marginBottom:6}}>How to create an Azure App Registration:</div>
                    <div>1. portal.azure.com → Entra ID → App Registrations → New Registration</div>
                    <div>2. API Permissions → Add Application permissions: User.Read.All, Directory.Read.All, UserAuthenticationMethod.ReadWrite.All, AuditLog.Read.All, MailboxSettings.ReadWrite → Grant Admin Consent</div>
                    <div>3. Certificates &amp; Secrets → New Client Secret → copy the value</div>
                  </div>
                  <div style={S.grid2}>
                    <div style={{gridColumn:"1/-1"}}><label style={S.label}>Azure Tenant ID</label><input style={{...inputStyle,fontFamily:"monospace",fontSize:13}} value={m365.azure_tenant_id} onChange={e=>setM("azure_tenant_id",e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/></div>
                    <div><label style={S.label}>Application (Client) ID</label><input style={{...inputStyle,fontFamily:"monospace",fontSize:13}} value={m365.azure_client_id} onChange={e=>setM("azure_client_id",e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"/></div>
                    <div><label style={S.label}>Client Secret Value</label><input type="password" style={{...inputStyle,fontSize:13}} value={m365.azure_client_secret} onChange={e=>setM("azure_client_secret",e.target.value)} placeholder="••••••••••••••••"/></div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center",marginTop:4}}>
                    <button onClick={testM365} disabled={testing||!m365.azure_tenant_id||!m365.azure_client_id||!m365.azure_client_secret} style={{...S.btnBlue,background:"#0f4c8a",opacity:(!m365.azure_tenant_id||!m365.azure_client_id||!m365.azure_client_secret)?0.5:1}}>
                      {testing?"Testing…":"🔌 Test Connection"}
                    </button>
                    {testResult?.ok&&<div style={{display:"flex",alignItems:"center",gap:6,color:"var(--badge-green)",fontSize:13}}><CheckCircle size={14}/> Connected — {testResult.tenant_name} · {testResult.licensed_users} users</div>}
                  </div>
                  {testResult?.domains?.length>0&&(
                    <div style={{marginTop:12,background:"rgba(22,163,74,0.08)",border:"1px solid rgba(22,163,74,0.2)",borderRadius:8,padding:"10px 14px"}}>
                      <div style={{fontSize:12,color:"var(--badge-green)",fontWeight:700,marginBottom:6}}>Verified Domains:</div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {testResult.domains.map(d=><span key={d} style={{background:"rgba(22,163,74,0.15)",color:"var(--badge-green)",border:"1px solid rgba(22,163,74,0.3)",borderRadius:4,padding:"2px 10px",fontSize:12,fontFamily:"monospace"}}>{d}</span>)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step===2&&(
            <div>
              <div style={S.sectionH}>Domain Configuration</div>
              <div style={{marginBottom:16}}>
                <label style={S.label}>Primary Domain</label>
                {domain.domains.length>0
                  ? <select style={{...inputStyle,cursor:"pointer"}} value={domain.domain} onChange={e=>setD("domain",e.target.value)}><option value="">Select domain</option>{domain.domains.map(d=><option key={d} value={d}>{d}</option>)}</select>
                  : <input style={inputStyle} value={domain.domain} onChange={e=>setD("domain",e.target.value)} placeholder="acmecorp.com"/>
                }
              </div>
              <div style={{padding:16,background:"var(--t-surface-mid)",border:"1px solid var(--t-border-strong)",borderRadius:10}}>
                <div style={{fontWeight:600,color:"var(--t-text-primary)",marginBottom:8,fontSize:14}}>👥 Import Users</div>
                <div style={{fontSize:13,color:"var(--t-text-label)",lineHeight:1.6}}>{m365.enabled?"User import will be available after the client is created on the final step.":"Add contacts manually from the client page after setup."}</div>
              </div>
            </div>
          )}

          {step===3&&(
            <div>
              <div style={S.sectionH}>Client Portal Branding</div>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Portal URL Slug</label>
                <div style={{display:"flex",alignItems:"center",background:"var(--t-surface-mid)",border:"1px solid var(--t-border-strong)",borderRadius:8,overflow:"hidden"}}>
                  <span style={{padding:"0 12px",color:"var(--t-text-deep)",fontSize:12,borderRight:"1px solid #334155",whiteSpace:"nowrap"}}>/clientportal/</span>
                  <input style={{...inputStyle,border:"none",borderRadius:0}} value={branding.portal_slug} onChange={e=>setBr("portal_slug",e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-"))} placeholder={slugify(basic.company_name||"client")}/>
                </div>
              </div>
              <div style={{marginBottom:12}}><label style={S.label}>Welcome Message</label><textarea style={{...inputStyle,resize:"vertical"}} rows={2} value={branding.welcome_msg} onChange={e=>setBr("welcome_msg",e.target.value)}/></div>
              <div style={S.grid2}>
                {[["Primary Color","primary_color"],["Background Color","accent_color"]].map(([lbl,key])=>(
                  <div key={key}>
                    <label style={S.label}>{lbl}</label>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:36,height:36,borderRadius:8,background:branding[key],border:"1px solid var(--t-border-strong)",flexShrink:0,overflow:"hidden",position:"relative"}}>
                        <input type="color" value={branding[key]} onChange={e=>setBr(key,e.target.value)} style={{position:"absolute",inset:"-4px",width:"calc(100%+8px)",height:"calc(100%+8px)",border:"none",cursor:"pointer",padding:0}}/>
                      </div>
                      <input style={inputStyle} value={branding[key]} onChange={e=>setBr(key,e.target.value)}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step===4&&(
            <div>
              <div style={S.sectionH}>Assignment</div>
              <div style={{marginBottom:14}}>
                <label style={S.label}>Account Manager</label>
                <select style={{...inputStyle,cursor:"pointer"}} value={assign.account_manager} onChange={e=>setA("account_manager",e.target.value)}>
                  <option value="">No account manager</option>
                  {agents.map(a=><option key={a.id} value={a.id}>{a.first_name} {a.last_name} ({a.role})</option>)}
                </select>
              </div>
              <div style={{marginBottom:14}}>
                <label style={S.label}>SLA Policy</label>
                <select style={{...inputStyle,cursor:"pointer"}} value={assign.sla_policy_id} onChange={e=>setA("sla_policy_id",e.target.value)}>
                  <option value="">No SLA policy</option>
                  {policies.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
          )}

          {step===5&&(
            <div>
              {createdClientId?(
                <div style={{textAlign:"center",padding:"20px 0"}}>
                  <div style={{fontSize:56,marginBottom:16}}>🎉</div>
                  <h2 style={{color:"var(--badge-green)",margin:"0 0 8px",fontSize:22}}>Client Created!</h2>
                  <p style={{color:"var(--t-text-label)",fontSize:14,marginBottom:24}}>{basic.company_name} has been set up successfully.</p>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,textAlign:"left",marginBottom:24}}>
                    {[{label:"Company",value:basic.company_name},{label:"Domain",value:domain.domain||"Not set"},{label:"M365",value:m365.enabled?"Connected":"Not configured"},{label:"Portal",value:branding.portal_slug?`support.midtc.com/clientportal/${branding.portal_slug}`:"Not configured"}].map(r=>(
                      <div key={r.label} style={{background:"var(--t-surface-mid)",border:"1px solid var(--t-border-strong)",borderRadius:8,padding:"10px 14px"}}>
                        <div style={{fontSize:11,color:"var(--t-text-deep)",marginBottom:4}}>{r.label.toUpperCase()}</div>
                        <div style={{fontSize:13,color:"var(--t-text-primary)",fontWeight:600,wordBreak:"break-all"}}>{r.value}</div>
                      </div>
                    ))}
                  </div>
                  {m365.enabled&&<button onClick={()=>setShowImport(true)} style={{...S.btnBlue,display:"inline-flex",alignItems:"center",gap:8}}><svg width="14" height="14" viewBox="0 0 21 21" fill="none"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>Import M365 Users</button>}
                </div>
              ):(
                <div>
                  <div style={S.sectionH}>Review & Create</div>
                  {[{label:"Company Name",value:basic.company_name},{label:"Phone",value:basic.phone||"—"},{label:"Address",value:[basic.address,basic.city,basic.state,basic.zip].filter(Boolean).join(", ")||"—"},{label:"M365",value:m365.enabled?`Tenant: ${m365.azure_tenant_id||"not set"}`:"Manual setup"},{label:"Domain",value:domain.domain||"—"},{label:"Portal URL",value:branding.portal_slug?`/clientportal/${branding.portal_slug}`:"—"},{label:"SLA Policy",value:policies.find(p=>p.id===assign.sla_policy_id)?.name||"None"},{label:"Account Mgr",value:agents.find(a=>a.id===assign.account_manager)?`${agents.find(a=>a.id===assign.account_manager).first_name} ${agents.find(a=>a.id===assign.account_manager).last_name}`:"None"}].map(r=>(
                    <div key={r.label} style={{display:"flex",justifyContent:"space-between",padding:"10px 14px",borderBottom:"1px solid var(--t-border-mid)",fontSize:13}}>
                      <span style={{color:"var(--t-text-label)"}}>{r.label}</span>
                      <span style={{color:"var(--t-text-primary)",fontWeight:500,maxWidth:"60%",textAlign:"right",wordBreak:"break-all"}}>{r.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={S.footer}>
          {step<5&&!createdClientId?(
            <>
              <button onClick={()=>{if(step>0)setStep(s=>s-1);else onClose();}} style={S.btnGhost}>{step===0?"Cancel":"← Back"}</button>
              {step<4
                ? <button onClick={()=>{setError("");setStep(s=>s+1);}} disabled={!canNext()} style={{...S.btnBlue,opacity:canNext()?1:0.5}}>Next →</button>
                : <button onClick={handleCreate} disabled={saving||!basic.company_name} style={S.btnBlue}>{saving?"Creating…":"✅ Create Client"}</button>
              }
            </>
          ):(
            <button onClick={onClose} style={S.btnBlue}>Done</button>
          )}
        </div>
      </div>
      {showImport&&createdClientId&&(
        <M365ImportModal clientId={createdClientId} clientName={basic.company_name} onClose={()=>setShowImport(false)} onImported={()=>setShowImport(false)}/>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  CLIENT CARD — rich preview with logo mark, stats, mini bars
// ─────────────────────────────────────────────────────────────
const PALETTE = [
  { g: "linear-gradient(135deg,#312e81,#6366f1)", glow: "rgba(99,102,241,0.4)",  accent: "#a5b4fc" },
  { g: "linear-gradient(135deg,#0e7490,#22d3ee)", glow: "rgba(34,211,238,0.4)",  accent: "#67e8f9" },
  { g: "linear-gradient(135deg,#7e22ce,#a855f7)", glow: "rgba(168,85,247,0.4)",  accent: "#d8b4fe" },
  { g: "linear-gradient(135deg,#065f46,#10b981)", glow: "rgba(16,185,129,0.4)",  accent: "#6ee7b7" },
  { g: "linear-gradient(135deg,#9a3412,#fb923c)", glow: "rgba(251,146,60,0.4)",  accent: "#fdba74" },
  { g: "linear-gradient(135deg,#831843,#ec4899)", glow: "rgba(236,72,153,0.4)",  accent: "#f9a8d4" },
];
function paletteFor(name) {
  if (!name) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function ClientCard({ client, manager, expanded, contacts, isLoadingContacts, onToggle, onOpenClient, onImportM365, onAddContact, onContactClick, contactForm, setContactForm, showNewContact, setShowNewContact, onSaveContact }) {
  const pal = paletteFor(client.company_name);
  const openT     = Number(client.open_tickets || 0);
  const totalT    = Number(client.total_tickets || 0);
  const onlineD   = Number(client.online_devices || 0);
  const totalD    = Number(client.total_devices || 0);
  const devicePct = totalD > 0 ? Math.round((onlineD / totalD) * 100) : null;
  const isM365    = !!client.azure_tenant_id;
  const hasIssues = openT > 0;
  const isHealthy = totalD > 0 && onlineD === totalD && openT === 0;

  return (
    <div style={{
      background: tokens.surface.panel,
      border: `1px solid ${tokens.border.soft}`,
      borderRadius: tokens.radius.md,
      overflow: "hidden",
      boxShadow: tokens.shadow.panel,
      transition: "all 0.18s",
      position: "relative",
      display: "flex", flexDirection: "column",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(99,102,241,0.3)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.3), 0 0 24px ${pal.glow}30`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = tokens.border.soft; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = tokens.shadow.panel; }}>

      {/* top sheen */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: tokens.shadow.cardSheen, pointerEvents: "none" }} />

      {/* HEAD ROW — logo, name, status pill */}
      <div style={{ padding: "16px 18px 12px", display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}
        onClick={() => onOpenClient(client.id)}>
        <div style={{
          width: 46, height: 46, borderRadius: 12, flexShrink: 0,
          background: pal.g,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 4px 14px ${pal.glow}, inset 0 1px 0 rgba(255,255,255,0.18)`,
          position: "relative", overflow: "hidden",
        }}>
          <Building2 size={22} color="#fff" />
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.18), transparent 55%)" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: tokens.text.primary, letterSpacing: "-0.2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
              {client.company_name}
            </div>
            {isHealthy && (
              <span title="All systems healthy" style={{ width: 8, height: 8, borderRadius: "50%", background: tokens.accent.green, boxShadow: `0 0 6px ${tokens.accent.green}` }} />
            )}
            {hasIssues && (
              <span title={`${openT} open ticket${openT !== 1 ? "s" : ""}`} style={{ width: 8, height: 8, borderRadius: "50%", background: tokens.accent.amber, boxShadow: `0 0 6px ${tokens.accent.amber}` }} />
            )}
          </div>
          <div style={{ fontSize: 12, color: tokens.text.faint, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>{client.domain || "no domain set"}</span>
            {isM365 && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: "rgba(99,102,241,0.15)", color: tokens.accent.indigo2,
                border: "1px solid rgba(99,102,241,0.3)", borderRadius: 99,
                padding: "1px 8px", fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
              }}>
                <Cloud size={9} /> M365
              </span>
            )}
            {client.industry && (
              <span style={{ fontSize: 10, color: tokens.text.deep, padding: "1px 7px", border: `1px solid ${tokens.border.soft}`, borderRadius: 99 }}>{client.industry}</span>
            )}
          </div>
        </div>
      </div>

      {/* STAT GRID — 4 mini KPIs */}
      <div style={{ padding: "0 18px 14px", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
        <StatTile icon={<Ticket size={11} />} value={openT} label="Open" color={openT > 0 ? tokens.accent.amber : tokens.text.faint} glow={openT > 0} />
        <StatTile icon={<Monitor size={11} />} value={totalD > 0 ? `${onlineD}/${totalD}` : "—"} label="Devices" color={tokens.accent.blue} />
        <StatTile icon={<Users size={11} />} value={contacts?.length ?? "—"} label="Contacts" color={tokens.accent.violet2} />
        <StatTile icon={<Activity size={11} />} value={totalT} label="Total" color={tokens.text.muted} />
      </div>

      {/* DEVICE HEALTH BAR */}
      {totalD > 0 && (
        <div style={{ padding: "0 18px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, color: tokens.text.faint, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
            <span>Device Health</span>
            <span style={{ color: devicePct >= 90 ? tokens.accent.green : devicePct >= 70 ? tokens.accent.amber : tokens.accent.red }}>
              {devicePct}%
            </span>
          </div>
          <div style={{ height: 5, background: "rgba(148,163,184,0.1)", borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${devicePct}%`,
              background: devicePct >= 90 ? "linear-gradient(90deg,#10b981,#34d399)" : devicePct >= 70 ? "linear-gradient(90deg,#f59e0b,#fbbf24)" : "linear-gradient(90deg,#dc2626,#f87171)",
              borderRadius: 99,
              boxShadow: `0 0 8px ${devicePct >= 90 ? "rgba(52,211,153,0.4)" : devicePct >= 70 ? "rgba(251,191,36,0.4)" : "rgba(248,113,113,0.4)"}`,
              transition: "width 0.4s",
            }} />
          </div>
        </div>
      )}

      {/* FOOTER — account manager + actions */}
      <div style={{ marginTop: "auto", padding: "10px 14px", borderTop: `1px solid ${tokens.border.soft}`, background: "rgba(15,23,42,0.3)", display: "flex", alignItems: "center", gap: 8 }}>
        {manager ? (
          <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, flex: 1 }} title={`Account Manager: ${manager.first_name} ${manager.last_name}`}>
            <MiniAvatar name={`${manager.first_name} ${manager.last_name}`} size={20} />
            <div style={{ minWidth: 0, lineHeight: 1.2 }}>
              <div style={{ fontSize: 9, color: tokens.text.faint, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Mgr</div>
              <div style={{ fontSize: 11, color: tokens.text.body, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 100 }}>{manager.first_name} {manager.last_name?.[0] || ""}</div>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, fontSize: 11, color: tokens.text.deep, fontStyle: "italic" }}>No account mgr</div>
        )}

        <button onClick={(e) => { e.stopPropagation(); onImportM365(client); }} title="Configure / Import M365"
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, borderRadius: 7,
            background: tokens.surface.input, border: `1px solid ${tokens.border.soft}`,
            cursor: "pointer", flexShrink: 0, transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(99,102,241,0.4)"; e.currentTarget.style.background = "rgba(99,102,241,0.08)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = tokens.border.soft; e.currentTarget.style.background = tokens.surface.input; }}>
          <svg width="13" height="13" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#f25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
            <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
          </svg>
        </button>

        <button onClick={(e) => { e.stopPropagation(); onToggle(client.id); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, fontFamily: "inherit",
            background: expanded ? "rgba(99,102,241,0.12)" : tokens.surface.input,
            color: expanded ? tokens.accent.indigo2 : tokens.text.muted,
            border: `1px solid ${expanded ? "rgba(99,102,241,0.3)" : tokens.border.soft}`,
            cursor: "pointer", transition: "all 0.15s",
          }}>
          <Users size={11} /> {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>

        <button onClick={(e) => { e.stopPropagation(); onOpenClient(client.id); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, fontFamily: "inherit",
            background: tokens.gradient.primary, color: "#fff", border: "none",
            cursor: "pointer", boxShadow: "0 2px 8px rgba(99,102,241,0.35), inset 0 1px 0 rgba(255,255,255,0.15)",
          }}>
          Open <ChevronRight size={11} />
        </button>
      </div>

      {/* EXPANDED CONTACTS DRAWER */}
      {expanded && (
        <div style={{
          background: "var(--t-surface-mid)",
          borderTop: `1px solid ${tokens.border.soft}`,
          padding: 14,
          animation: "fadeIn 0.18s ease",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: tokens.text.label, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              {isLoadingContacts ? "Loading…" : `${contacts?.length ?? 0} Contact${contacts?.length !== 1 ? "s" : ""}`}
            </span>
            <button onClick={() => setShowNewContact(showNewContact === client.id ? null : client.id)}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                background: tokens.gradient.primary, color: "#fff",
                border: "none", padding: "5px 11px", borderRadius: 7,
                fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
              }}>
              <Plus size={11} /> Add
            </button>
          </div>
          {!isLoadingContacts && contacts?.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
              {contacts.map(c => (
                <div key={c.id} onClick={() => onContactClick(c.id)}
                  style={{
                    background: tokens.surface.panel,
                    border: `1px solid ${tokens.border.soft}`,
                    borderRadius: tokens.radius.sm, padding: "8px 11px",
                    cursor: "pointer", transition: "all 0.15s",
                    display: "flex", alignItems: "center", gap: 9,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(99,102,241,0.35)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = tokens.border.soft; }}>
                  <MiniAvatar name={`${c.first_name} ${c.last_name}`} size={26} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, color: tokens.text.primary, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.first_name} {c.last_name}
                    </div>
                    <div style={{ fontSize: 10, color: tokens.text.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.email}</div>
                  </div>
                  {c.job_title && (
                    <span style={{ fontSize: 9, color: tokens.text.deep, padding: "1px 7px", border: `1px solid ${tokens.border.soft}`, borderRadius: 99, whiteSpace: "nowrap" }}>{c.job_title}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {!isLoadingContacts && contacts !== null && contacts.length === 0 && (
            <div style={{ color: tokens.text.deep, fontSize: 12, textAlign: "center", padding: "8px 0" }}>No contacts yet.</div>
          )}
          {showNewContact === client.id && (
            <form onSubmit={onSaveContact} style={{
              marginTop: 12, background: tokens.surface.panelHi,
              border: `1px solid ${tokens.border.soft}`, borderRadius: tokens.radius.sm,
              padding: 12, boxShadow: tokens.shadow.panelHi,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: tokens.text.label, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>New Contact</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                {[
                  { key: "first_name", label: "First *", required: true },
                  { key: "last_name", label: "Last *", required: true },
                  { key: "email", label: "Email *", required: true, type: "email", span: 2 },
                  { key: "phone", label: "Phone" },
                  { key: "job_title", label: "Job title" },
                ].map(f => (
                  <div key={f.key} style={f.span === 2 ? { gridColumn: "1/-1" } : {}}>
                    <label style={{ fontSize: 10, color: tokens.text.muted, display: "block", marginBottom: 3 }}>{f.label}</label>
                    <input type={f.type || "text"} required={f.required} value={contactForm[f.key]}
                      onChange={e => setContactForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ ...sx.input, padding: "6px 10px", fontSize: 12 }} />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="submit" style={{
                  background: tokens.gradient.primary, color: "#fff", border: "none",
                  padding: "6px 14px", borderRadius: 7, fontWeight: 700, fontSize: 11,
                  cursor: "pointer", fontFamily: "inherit",
                  boxShadow: tokens.shadow.primaryGlow,
                }}>Save</button>
                <button type="button" onClick={() => setShowNewContact(null)} style={{
                  background: "transparent", color: tokens.text.muted,
                  border: `1px solid ${tokens.border.mid}`,
                  padding: "6px 12px", borderRadius: 7, fontSize: 11,
                  cursor: "pointer", fontFamily: "inherit",
                }}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

// Small stat tile inside a card
const StatTile = ({ icon, value, label, color, glow }) => (
  <div style={{
    background: "rgba(15,23,42,0.4)",
    border: `1px solid ${tokens.border.soft}`,
    borderRadius: 8, padding: "8px 10px",
    boxShadow: glow ? `0 0 10px ${color}22, inset 0 1px 0 rgba(255,255,255,0.04)` : "inset 0 1px 0 rgba(255,255,255,0.03)",
    transition: "all 0.18s",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 4, color, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.85, marginBottom: 3 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 16, fontWeight: 800, color, lineHeight: 1, letterSpacing: "-0.3px" }}>{value ?? "—"}</div>
  </div>
);

// Horizontal bar chart: tickets per client
function ClientTicketsBar({ clients, onClick }) {
  const sorted = [...clients].sort((a, b) => Number(b.open_tickets || 0) - Number(a.open_tickets || 0));
  const max = Math.max(...sorted.map(c => Number(c.open_tickets || 0)), 1);
  if (sorted.length === 0 || max === 1 && sorted.every(c => !c.open_tickets)) {
    return (
      <div style={{ padding: 30, textAlign: "center", color: tokens.text.faint, fontSize: 12 }}>
        <Sparkles size={28} style={{ opacity: 0.4, marginBottom: 8 }} /><br />
        No open tickets across any client — everyone's quiet 🎉
      </div>
    );
  }
  return (
    <div style={{ padding: "8px 4px 12px" }}>
      {sorted.slice(0, 8).map((c, i) => {
        const open = Number(c.open_tickets || 0);
        const pct = (open / max) * 100;
        const pal = paletteFor(c.company_name);
        return (
          <div key={c.id} onClick={() => onClick(c.id)} style={{
            padding: "8px 14px", cursor: "pointer", borderRadius: 6,
            transition: "background 0.15s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.06)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5, gap: 8 }}>
              <span style={{ fontSize: 12, color: tokens.text.body, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                {c.company_name}
              </span>
              <span style={{ fontSize: 11, color: open > 0 ? tokens.accent.amber : tokens.text.faint, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {open} open
              </span>
            </div>
            <div style={{ height: 5, background: "rgba(148,163,184,0.1)", borderRadius: 99, overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${pct}%`,
                background: open > 0 ? pal.g : "rgba(148,163,184,0.2)",
                borderRadius: 99,
                boxShadow: open > 0 ? `0 0 6px ${pal.glow}` : "none",
                transition: "width 0.4s",
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Donut: M365 vs Manual
function ConnectivityDonut({ m365Count, manualCount }) {
  const total = m365Count + manualCount;
  if (total === 0) return <div style={{ padding: 30, textAlign: "center", color: tokens.text.faint, fontSize: 12 }}>No clients yet.</div>;
  const r = 32, c = 50, stroke = 12;
  const circumference = 2 * Math.PI * r;
  const m365Frac = m365Count / total;
  const m365Dash = m365Frac * circumference;
  return (
    <div style={{ padding: "16px 16px 18px", display: "flex", alignItems: "center", gap: 18 }}>
      <svg width="110" height="110" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
        <defs>
          <linearGradient id="m365grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(148,163,184,0.1)" strokeWidth={stroke} />
        <circle cx={c} cy={c} r={r} fill="none" stroke="url(#m365grad)" strokeWidth={stroke}
          strokeDasharray={`${m365Dash} ${circumference - m365Dash}`}
          transform={`rotate(-90 ${c} ${c})`}
          strokeLinecap="butt"
          style={{ filter: "drop-shadow(0 0 4px rgba(99,102,241,0.5))" }}
        />
        <text x={c} y={c - 1} textAnchor="middle" fontSize="18" fontWeight="800" fill="#f1f5f9">{Math.round(m365Frac * 100)}%</text>
        <text x={c} y={c + 11} textAnchor="middle" fontSize="6.5" fill="#56677e" fontWeight="600" letterSpacing="0.05em">CONNECTED</text>
      </svg>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: "linear-gradient(135deg,#6366f1,#a855f7)", flexShrink: 0, boxShadow: "0 0 4px rgba(99,102,241,0.5)" }} />
          <span style={{ color: tokens.text.body, flex: 1 }}>M365 Integrated</span>
          <span style={{ color: tokens.text.muted, fontWeight: 700 }}>{m365Count}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(148,163,184,0.3)", flexShrink: 0 }} />
          <span style={{ color: tokens.text.body, flex: 1 }}>Manual Setup</span>
          <span style={{ color: tokens.text.muted, fontWeight: 700 }}>{manualCount}</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  CLIENTS PAGE — bento layout
// ─────────────────────────────────────────────────────────────
export default function Clients() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [clients,         setClients]         = useState([]);
  const [policies,        setPolicies]        = useState([]);
  const [agents,          setAgents]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [showWizard,      setShowWizard]      = useState(false);
  const [expanded,        setExpanded]        = useState(null);
  const [contacts,        setContacts]        = useState({});
  const [loadingContacts, setLoadingContacts] = useState({});
  const [showNewContact,  setShowNewContact]  = useState(null);
  const [showCreds,       setShowCreds]       = useState(null);
  const [showImport,      setShowImport]      = useState(null);
  const [importClient,    setImportClient]    = useState(null);
  const [contactForm,     setContactForm]     = useState({ first_name:"", last_name:"", email:"", phone:"", job_title:"" });

  // filter / sort state
  const [search, setSearch] = useState("");
  const [sort,   setSort]   = useState("name"); // name | tickets | devices | recent
  const [filter, setFilter] = useState("all");  // all | m365 | issues | healthy

  useEffect(() => {
    Promise.all([getClients(), getSLAPolicies(), getAgents()])
      .then(([c,p,a]) => {
        setClients(normalizeArray(c.data??c));
        setPolicies(normalizeArray(p.data??p));
        setAgents(normalizeArray(a.data??a));
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchContacts = async (clientId) => {
    setLoadingContacts(p=>({...p,[clientId]:true}));
    try { const raw = await getContacts(clientId); setContacts(p=>({...p,[clientId]:normalizeArray(raw.data??raw)})); }
    catch { setContacts(p=>({...p,[clientId]:[]})); }
    finally { setLoadingContacts(p=>({...p,[clientId]:false})); }
  };

  const toggleExpand = async (clientId) => {
    if (expanded===clientId) { setExpanded(null); return; }
    setExpanded(clientId);
    if (!contacts[clientId]) await fetchContacts(clientId);
  };

  const handleNewContact = async (e, clientId) => {
    e.preventDefault();
    try { await createContact({...contactForm,client_id:clientId}); await fetchContacts(clientId); setShowNewContact(null); setContactForm({first_name:"",last_name:"",email:"",phone:"",job_title:""}); }
    catch (err) { console.error(err); }
  };

  // Lazy-load contacts counts for all clients (one-shot, after initial load) so the card chip can show real numbers
  useEffect(() => {
    if (!clients.length) return;
    clients.forEach(c => {
      if (contacts[c.id] === undefined) {
        getContacts(c.id)
          .then(r => setContacts(p => ({ ...p, [c.id]: normalizeArray(r.data ?? r) })))
          .catch(() => setContacts(p => ({ ...p, [c.id]: [] })));
      }
    });
  }, [clients]); // eslint-disable-line

  const agentById = useMemo(() => Object.fromEntries(agents.map(a => [a.id, a])), [agents]);

  // ── KPIs ───────────────────────────────────────────────────
  const totalClients = clients.length;
  const totalOpen    = clients.reduce((s, c) => s + Number(c.open_tickets || 0), 0);
  const totalDevices = clients.reduce((s, c) => s + Number(c.total_devices || 0), 0);
  const onlineDevices= clients.reduce((s, c) => s + Number(c.online_devices || 0), 0);
  const m365Count    = clients.filter(c => c.azure_tenant_id).length;
  const healthyCount = clients.filter(c => Number(c.open_tickets || 0) === 0).length;
  const issuesCount  = totalClients - healthyCount;
  const devicePct    = totalDevices > 0 ? Math.round((onlineDevices / totalDevices) * 100) : 100;

  // ── filter + sort pipeline ─────────────────────────────────
  const visibleClients = useMemo(() => {
    let arr = clients;
    const q = search.trim().toLowerCase();
    if (q) arr = arr.filter(c =>
      c.company_name?.toLowerCase().includes(q) ||
      c.domain?.toLowerCase().includes(q)       ||
      c.industry?.toLowerCase().includes(q)
    );
    if (filter === "m365")    arr = arr.filter(c => c.azure_tenant_id);
    if (filter === "issues")  arr = arr.filter(c => Number(c.open_tickets || 0) > 0);
    if (filter === "healthy") arr = arr.filter(c => Number(c.open_tickets || 0) === 0);

    arr = [...arr];
    if (sort === "tickets")   arr.sort((a, b) => Number(b.open_tickets || 0) - Number(a.open_tickets || 0));
    else if (sort === "devices") arr.sort((a, b) => Number(b.total_devices || 0) - Number(a.total_devices || 0));
    else if (sort === "recent")  arr.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
    else arr.sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
    return arr;
  }, [clients, search, sort, filter]);

  if (loading) return <FullPageSpinner />;

  // ── small chip-style toggle ────────────────────────────────
  const chip = (active, onClick, label, count, color = tokens.accent.indigo2) => (
    <button onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 12px", borderRadius: 99, fontSize: 12, fontWeight: 600, fontFamily: "inherit",
      background: active ? "rgba(99,102,241,0.15)" : tokens.surface.input,
      color: active ? color : tokens.text.muted,
      border: `1px solid ${active ? "rgba(99,102,241,0.35)" : tokens.border.soft}`,
      cursor: "pointer", transition: "all 0.15s",
      boxShadow: active ? "0 0 10px rgba(99,102,241,0.18)" : "none",
    }}>
      {label}
      {count != null && (
        <span style={{
          fontSize: 10, padding: "1px 6px", borderRadius: 99,
          background: active ? "rgba(99,102,241,0.25)" : "rgba(148,163,184,0.12)",
          color: active ? color : tokens.text.faint, fontWeight: 700,
        }}>{count}</span>
      )}
    </button>
  );

  return (
    <div style={{ padding: isMobile ? "14px 14px 24px" : "22px 22px 32px", maxWidth: 1600 }} className="fade-in">

      {/* HEADER */}
      <div style={{
        paddingBottom: 14, borderBottom: `1px solid ${tokens.border.soft}`,
        marginBottom: 18, display: "flex", alignItems: "flex-end",
        justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: tokens.text.primary, letterSpacing: "-0.5px" }}>
            Clients <span style={{ opacity: 0.4 }}>·</span> <span style={{ background: "linear-gradient(135deg,#818cf8,#22d3ee)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{totalClients}</span>
          </h1>
          <p style={{ color: tokens.text.faint, fontSize: 13, marginTop: 3 }}>
            {totalOpen} open ticket{totalOpen !== 1 ? "s" : ""}
            <span style={{ margin: "0 8px" }}>·</span>
            {onlineDevices} / {totalDevices} devices online
            <span style={{ margin: "0 8px" }}>·</span>
            {m365Count} M365-connected
          </p>
        </div>
        <button onClick={() => setShowWizard(true)} style={sx.buttonPrimary}>
          <Plus size={15} /> Add Client
        </button>
      </div>

      {/* KPI STRIP */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 18 }}>
        <KpiCard label="Total Clients" value={totalClients} icon={Building2} color={tokens.accent.indigo2}
          sub={`${m365Count} integrated`} />
        <KpiCard label="Open Tickets"  value={totalOpen}    icon={Ticket}     color={totalOpen > 0 ? tokens.accent.amber : tokens.accent.green}
          sub={totalOpen > 0 ? `Across ${issuesCount} client${issuesCount !== 1 ? "s" : ""}` : "All clear"}
          onClick={() => navigate("/tickets?status=Active")} />
        <KpiCard label="Devices Online" value={`${onlineDevices}/${totalDevices}`} icon={Monitor} color={tokens.accent.blue}
          sub={`${devicePct}% of fleet`}
          onClick={() => navigate("/devices")} />
        <KpiCard label="M365 Connected" value={m365Count} icon={Cloud}         color={tokens.accent.violet2}
          sub={`${Math.round((m365Count / Math.max(totalClients, 1)) * 100)}% of clients`} />
        <KpiCard label="Healthy"        value={healthyCount} icon={ShieldCheck} color={tokens.accent.green}
          sub="No open tickets" />
        <KpiCard label="Need Attention" value={issuesCount}  icon={AlertTriangle} color={issuesCount > 0 ? tokens.accent.red : tokens.text.faint}
          sub={issuesCount > 0 ? "Active issues" : "All clear"} />
      </div>

      {/* CHARTS ROW */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)", gap: 14, marginBottom: 18 }}>
        <Panel title="Open Tickets · By Client" action={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: tokens.text.faint }}>
            <BarChart3 size={11} /> Top {Math.min(8, totalClients)}
          </span>
        }>
          <ClientTicketsBar clients={clients} onClick={(id) => navigate(`/clients/${id}`)} />
        </Panel>

        <Panel title="Integration Coverage" action={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: tokens.accent.indigo2 }}>
            <Cloud size={11} /> M365
          </span>
        }>
          <ConnectivityDonut m365Count={m365Count} manualCount={totalClients - m365Count} />
        </Panel>
      </div>

      {/* SEARCH + FILTER + SORT BAR */}
      <Panel style={{ marginBottom: 14 }} bodyStyle={{ padding: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          {/* Search */}
          <div style={{ position: "relative", flex: isMobile ? "1 1 100%" : 1, minWidth: isMobile ? 0 : 220 }}>
            <Search size={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: tokens.text.deep, pointerEvents: "none" }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, domain, or industry…"
              style={{ ...sx.input, paddingLeft: 33, fontSize: 13 }}
            />
          </div>

          {/* Filter chips */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {chip(filter === "all",     () => setFilter("all"),     "All",       totalClients)}
            {chip(filter === "issues",  () => setFilter("issues"),  "Has Issues",issuesCount,  tokens.accent.amber)}
            {chip(filter === "healthy", () => setFilter("healthy"), "Healthy",   healthyCount, tokens.accent.green)}
            {chip(filter === "m365",    () => setFilter("m365"),    "M365",      m365Count,    tokens.accent.indigo2)}
          </div>

          {/* Sort */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ArrowUpDown size={13} style={{ color: tokens.text.faint }} />
            <select value={sort} onChange={e => setSort(e.target.value)} style={{
              ...sx.input, width: "auto", padding: "6px 28px 6px 10px", fontSize: 12, cursor: "pointer",
              appearance: "none", backgroundImage: "linear-gradient(45deg,transparent 50%,#56677e 50%),linear-gradient(135deg,#56677e 50%,transparent 50%)",
              backgroundPosition: "calc(100% - 13px) 50%,calc(100% - 8px) 50%", backgroundSize: "5px 5px,5px 5px", backgroundRepeat: "no-repeat",
            }}>
              <option value="name">A → Z</option>
              <option value="tickets">Most Tickets</option>
              <option value="devices">Most Devices</option>
              <option value="recent">Recently Updated</option>
            </select>
          </div>
        </div>
      </Panel>

      {/* CLIENT CARDS GRID */}
      {visibleClients.length === 0 ? (
        <Panel>
          <div style={{ padding: 48, textAlign: "center", color: tokens.text.deep }}>
            {clients.length === 0 ? (
              <>
                <Building2 size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontSize: 14, color: tokens.text.muted, marginBottom: 4 }}>No clients yet</div>
                <div style={{ fontSize: 12, color: tokens.text.faint }}>Click "Add Client" to get started.</div>
              </>
            ) : (
              <>
                <Filter size={32} style={{ opacity: 0.3, marginBottom: 10 }} />
                <div style={{ fontSize: 13, color: tokens.text.muted }}>No clients match your filters.</div>
                <button onClick={() => { setSearch(""); setFilter("all"); }} style={{ ...sx.buttonGhost, marginTop: 12, fontSize: 12 }}>
                  Clear filters
                </button>
              </>
            )}
          </div>
        </Panel>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 14 }}>
          {visibleClients.map(client => (
            <ClientCard
              key={client.id}
              client={client}
              manager={agentById[client.account_manager]}
              expanded={expanded === client.id}
              contacts={contacts[client.id] ?? null}
              isLoadingContacts={loadingContacts[client.id]}
              onToggle={toggleExpand}
              onOpenClient={(id) => navigate(`/clients/${id}`)}
              onImportM365={(c) => { setShowCreds(c); setImportClient(c); }}
              onContactClick={(id) => navigate(`/contacts/${id}`)}
              contactForm={contactForm}
              setContactForm={setContactForm}
              showNewContact={showNewContact}
              setShowNewContact={setShowNewContact}
              onSaveContact={(e) => handleNewContact(e, client.id)}
            />
          ))}
        </div>
      )}

      {/* MODALS */}
      {showWizard && (
        <NewClientWizard policies={policies} agents={agents}
          onClose={() => setShowWizard(false)}
          onCreated={(client) => setClients(p => [...p, client])} />
      )}

      {showCreds && importClient && (
        <M365CredentialsModal
          clientId={importClient.id}
          clientName={importClient.company_name}
          onClose={() => { setShowCreds(null); setImportClient(null); }}
          onSaved={() => { setShowCreds(null); setShowImport(importClient.id); }}
        />
      )}

      {showImport && importClient && (
        <M365ImportModal
          clientId={importClient.id}
          clientName={importClient.company_name}
          onClose={() => { setShowImport(null); setImportClient(null); }}
          onImported={() => { if (expanded === importClient.id) fetchContacts(importClient.id); }}
        />
      )}
    </div>
  );
}
